import type { RefObject } from "react";
import { useCallback, useEffect, useRef } from "react";
import { IS_SEQ_LOG_ENABLED, LogLevel, useSeqLog } from "@/hooks/telemetry";
import { TEAM_CARD_RENDER_RADIUS } from "./constants";

const LANDING_PERF_CAROUSEL_COMPONENT = "LandingPerf.Carousel";
const LANDING_PERF_CAROUSEL_MOUNT_MESSAGE = "carousel-mount";
const LANDING_PERF_CAROUSEL_SETTLE_MESSAGE = "scroll-settle";
const CAROUSEL_TRANSFORMS_PER_CARD = 8;

interface CarouselTelemetryGeometry {
  cardGap: number;
  cardHeight: number;
  cardWidth: number;
  isMobile: boolean;
  pitch: number;
}

interface UseCarouselTelemetryParams {
  centeredDoubledIndex: number;
  containerRef: RefObject<HTMLDivElement | null>;
  geometry: CarouselTelemetryGeometry;
  itemsLength: number;
  originalLength: number;
}

interface ScrollSampleState {
  eventCount: number;
  firstEventMs: number;
  lastScrollLeft: number;
}

interface GetEventsPerSecondParams {
  elapsedMs: number;
  eventCount: number;
}

function getEventsPerSecond({
  elapsedMs,
  eventCount,
}: GetEventsPerSecondParams): number | null {
  if (elapsedMs <= 0) return null;

  return (eventCount / elapsedMs) * 1000;
}

interface GetScrollDeltaParams {
  finalScrollLeft: number | null;
  startScrollLeft: number;
}

function getScrollDelta({
  finalScrollLeft,
  startScrollLeft,
}: GetScrollDeltaParams): number | null {
  if (finalScrollLeft == null) return null;

  return finalScrollLeft - startScrollLeft;
}

export function useCarouselTelemetry({
  centeredDoubledIndex,
  containerRef,
  geometry,
  itemsLength,
  originalLength,
}: UseCarouselTelemetryParams): void {
  const landingPerfCarouselLog = useSeqLog(LANDING_PERF_CAROUSEL_COMPONENT);
  const { cardGap, cardHeight, cardWidth, isMobile, pitch } = geometry;
  const scrollSampleStateRef = useRef<ScrollSampleState>({
    eventCount: 0,
    firstEventMs: 0,
    lastScrollLeft: 0,
  });

  const recordScrollSample = useCallback((scrollLeft: number): void => {
    if (!IS_SEQ_LOG_ENABLED) return;

    const state = scrollSampleStateRef.current;
    if (state.eventCount < 1) {
      state.firstEventMs = performance.now();
      state.lastScrollLeft = scrollLeft;
    }
    state.eventCount += 1;
  }, []);

  useEffect(() => {
    const scrollElement = containerRef.current;
    if (!scrollElement) return;

    const onScroll = (): void => {
      recordScrollSample(scrollElement.scrollLeft);
    };

    scrollElement.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scrollElement.removeEventListener("scroll", onScroll);
    };
  }, [containerRef, recordScrollSample]);

  useEffect(() => {
    if (!IS_SEQ_LOG_ENABLED) return;

    const state = scrollSampleStateRef.current;
    if (state.eventCount < 1) return;

    const nowMs = performance.now();
    const elapsedMs = nowMs - state.firstEventMs;
    const containerEl = containerRef.current;
    const finalScrollLeft = containerEl?.scrollLeft ?? null;

    landingPerfCarouselLog(LANDING_PERF_CAROUSEL_SETTLE_MESSAGE, {
      eventCount: state.eventCount,
      elapsedMs,
      eventsPerSecond: getEventsPerSecond({
        elapsedMs,
        eventCount: state.eventCount,
      }),
      startScrollLeft: state.lastScrollLeft,
      finalScrollLeft,
      deltaPx: getScrollDelta({
        finalScrollLeft,
        startScrollLeft: state.lastScrollLeft,
      }),
      centeredDoubledIndex,
    });

    state.eventCount = 0;
    state.firstEventMs = 0;
    state.lastScrollLeft = 0;
  }, [centeredDoubledIndex, containerRef, landingPerfCarouselLog]);

  useEffect(() => {
    const renderedCards = Math.min(itemsLength, TEAM_CARD_RENDER_RADIUS * 2 + 1);
    landingPerfCarouselLog(
      LANDING_PERF_CAROUSEL_MOUNT_MESSAGE,
      {
        teamsLength: originalLength,
        itemsLength,
        renderRadius: TEAM_CARD_RENDER_RADIUS,
        renderedCards,
        placeholderCards: Math.max(0, itemsLength - renderedCards),
        transformSubscribers: renderedCards * CAROUSEL_TRANSFORMS_PER_CARD,
        pitchPx: pitch,
        cardWidthPx: cardWidth,
        cardHeightPx: cardHeight,
        cardGapPx: cardGap,
        isMobile,
      },
      LogLevel.Info,
    );
  }, [
    cardGap,
    cardHeight,
    cardWidth,
    isMobile,
    pitch,
    itemsLength,
    landingPerfCarouselLog,
    originalLength,
  ]);

}
